// Third-party
import React, { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import Dotdotdot from 'react-dotdotdot';
import { camelCase } from 'lodash';
import classNames from 'classnames';

// Namespaces
import * as ProposalNS from '@/modules/proposal/Proposal';

// Utils
import { formatLocalizedString, formatMessage } from '@/utils/i18n';
import { userHasPermissionEnabled, PERMISSIONS } from '@/utils/permissions';
import { PROPOSAL_ITEM_PURPOSE } from '@/modules/proposal/utils/constants';
import { DragItemTypes, DropItemPosition } from '@/utils/drag-and-drop';
import useScreenSize from '@/hooks/useScreenSize';

// Components
import { HoldingIcon } from '@/views/components/common';
import { Select, Checkbox } from '@/views/components/inputs';
import { InfoTooltip } from '@/views/components/tooltips';
import { SelectItem } from '@/views/components/inputs/Select';
import { ProposalItemDragPreviewItem } from '@/views/components/misc/drag-and-drop/ProposalItemDragPreview';
import ComparisonItem from '@/types/comparison-item';
import User from '@/types/user';
import { ComparisonItemCategory } from '@/redux/modules/types/comparison';

type Props = {
  showIcon: boolean;
  item: ComparisonItem;
  proposal: ProposalNS.Proposal;
  user: User;
  disabled: boolean;
  selected: boolean;
  showProposalItemPurposeSelector?: boolean;
  itemPurposeList: SelectItem[];
  onToggle: (item: ComparisonItem) => void;
  onChangeItemPurpose: (item: ComparisonItem, selectedItem?: SelectItem) => void;
  onChangeItemOrder: (itemId: string, referenceId: string, position?: DropItemPosition) => void;
  onItemPurposeFlagUpdate: (itemId: string, flags: string[]) => void;
};

const ProposalItemListerOption = (props: Props) => {
  const {
    item,
    disabled,
    selected,
    showIcon,
    proposal,
    user,
    showProposalItemPurposeSelector,
    itemPurposeList,
    onToggle,
    onChangeItemPurpose,
    onChangeItemOrder,
    onItemPurposeFlagUpdate,
  } = props;

  useEffect(() => {
    const flags = item.flags?.filter(flag => !Object.values<string>(PROPOSAL_ITEM_PURPOSE).includes(flag)) ?? [];

    if (item.category === ComparisonItemCategory.CURRENT) {
      onItemPurposeFlagUpdate(item._id, [...flags, PROPOSAL_ITEM_PURPOSE.CURRENT]);
    } else if (item.category === ComparisonItemCategory.RECOMMENDED) {
      onItemPurposeFlagUpdate(item._id, [...flags, PROPOSAL_ITEM_PURPOSE.RECOMMENDED]);
    }
  }, [item._id, item.category, item.flags, onItemPurposeFlagUpdate]);

  const name: string | undefined = formatLocalizedString(item.name);
  const proposalItem = proposal.items.find(i => i._id === item._id);
  const itemWrapperRef = useRef<HTMLDivElement>(null);

  const [dropPosition, setDropPosition] = useState<DropItemPosition | null>(null);

  const getMonitorDropPosition = (monitor: DropTargetMonitor) => {
    const clientOffset = monitor.getClientOffset();
    if (!clientOffset || !itemWrapperRef.current) {
      return null;
    }

    const clientRect = itemWrapperRef.current.getBoundingClientRect();

    if (clientOffset.y < clientRect.y + clientRect.height / 2) {
      return DropItemPosition.Before;
    }

    return DropItemPosition.After;
  };

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: DragItemTypes.ProposalItemOption,
      item: {
        _id: item._id,
        name: item.name,
        type: item.type,
        color: showIcon ? item.color : undefined,
        disabled,
        selected,
      } as ProposalItemDragPreviewItem,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [item, disabled, selected, dropPosition, showIcon],
  );

  const [{ isActive: isDropping }, drop] = useDrop(
    () => ({
      accept: DragItemTypes.ProposalItemOption,
      drop: (droppedItem: ProposalItemDragPreviewItem) => {
        if (!dropPosition) {
          return;
        }

        onChangeItemOrder(droppedItem._id, item._id, dropPosition);
      },
      hover: (dragItem, monitor) => {
        setDropPosition(getMonitorDropPosition(monitor));
      },
      collect: monitor => ({
        isActive: monitor.canDrop() && monitor.isOver() && monitor.getItem() !== null && monitor.getItem()._id !== item._id,
      }),
    }),
    [dropPosition, item],
  );

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  });

  if (!name) {
    return null;
  }

  const element = (
    <>
      {showIcon && <HoldingIcon type={item.type} color={item.color} />}
      <Dotdotdot useNativeClamp clamp={2}>
        {formatLocalizedString(item.name)}
      </Dotdotdot>
    </>
  );

  return (
    <div ref={itemWrapperRef}>
      <div ref={drop} className="proposal-item-lister-option">
        <div
          ref={drag}
          data-private
          data-dd-privacy="mask"
          className={classNames('proposal-item-lister-option-content proposal-checklist-option', {
            disabled,
            dragging: isDragging,
            [`dropping-${dropPosition?.toLowerCase()}`]: isDropping,
          })}
        >
          <div className="proposal-item-drag-handle">
            <i className="icon-drag-vertical" />
          </div>

          <Checkbox disabled={disabled} selected={selected} label={element} onChange={() => onToggle(item)} />

          {proposalItem?.flags?.includes(PROPOSAL_ITEM_PURPOSE.CURRENT) && (
            <InfoTooltip message={formatMessage('Proposal.ProposalItemPurposeSelect.InfoTooltip')} />
          )}

          {showProposalItemPurposeSelector && selected && userHasPermissionEnabled(user, PERMISSIONS.PROPOSAL_ITEM_PURPOSE) && (
            <Select
              className="proposal-item-purpose-select"
              label={formatMessage('Proposal.ProposalItemPurposeSelect.Label')}
              hideLabel
              disabled={disabled}
              selectedItem={itemPurposeList.find(purpose => proposalItem?.flags?.includes(purpose.value)) ?? itemPurposeList[0]}
              items={itemPurposeList}
              onChange={({ selectedItem }) => onChangeItemPurpose(item, selectedItem)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProposalItemListerOption;
